import React, { useRef, useEffect, useCallback, useState } from 'react';
import FilterDropdown from '../FilterDropdown/FilterDropdown';
import HyperliquidPromo from '../PromoButtons/HyperliquidPromo';
import BitunixPromo from '../PromoButtons/BitunixPromo';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import './LivePriceTicker.css';

// Below this viewport width the HL pill collapses to a tiny "4% OFF" badge
// (matches the @media rule in the .css). On those viewports the affordance
// is two-tap: first tap reveals the full "Trade on Hyperliquid" label, the
// second tap follows the link. On desktop the label is always visible so
// the link works on the first click as before.
const PILL_COLLAPSE_BREAKPOINT = 640;

function LivePriceTicker({ prices, formatPrice, filters, setWhaleTradesMinVolume, setLiquidationsMinVolume, setSelectedCoins, toggleCategory, clearCategories, tickerCoins }) {
  const containerRef = useRef(null);
  const latestPricesRef = useRef({});
  const formatPriceRef = useRef(formatPrice);
  const lastWrittenRef = useRef({});
  const pillRef = useRef(null);
  const [pillExpanded, setPillExpanded] = useState(false);
  const showWeex = useFeatureFlag('weex-promo', true);

  // Collapse the pill again when the user taps anywhere else, so the next
  // visit to it starts in the "preview" state instead of being stuck open.
  useEffect(() => {
    if (!pillExpanded) return undefined;
    const off = (e) => {
      if (pillRef.current && !pillRef.current.contains(e.target)) {
        setPillExpanded(false);
      }
    };
    document.addEventListener('mousedown', off);
    document.addEventListener('touchstart', off, { passive: true });
    return () => {
      document.removeEventListener('mousedown', off);
      document.removeEventListener('touchstart', off);
    };
  }, [pillExpanded]);

  const handlePillClick = useCallback((e) => {
    // Desktop / wide viewport — pill already shows the full label, so the
    // click should just follow the link.
    if (window.innerWidth > PILL_COLLAPSE_BREAKPOINT) return;
    // Mobile, collapsed → first tap expands instead of navigating.
    if (!pillExpanded) {
      e.preventDefault();
      setPillExpanded(true);
    }
    // Mobile, already expanded → fall through and let the <a> open the link.
  }, [pillExpanded]);

  const hasPrices = Object.keys(prices).length > 0;

  latestPricesRef.current = prices;
  formatPriceRef.current = formatPrice;

  // Flush prices to DOM once per second
  useEffect(() => {
    if (!hasPrices || !tickerCoins.length) return;

    function flush() {
      const el = containerRef.current;
      if (!el) return;
      const p = latestPricesRef.current;
      const fmt = formatPriceRef.current;

      for (let i = 0; i < tickerCoins.length; i++) {
        const sym = tickerCoins[i].symbol;
        if (!p[sym]) continue;
        const text = fmt(p[sym]);
        if (lastWrittenRef.current[sym] === text) continue;
        lastWrittenRef.current[sym] = text;
        const node = el.querySelector(`[data-price="${sym}"]`);
        if (node) node.textContent = text;
      }
    }

    flush();
    const id = setInterval(flush, 1000);
    return () => clearInterval(id);
  }, [hasPrices, tickerCoins]);

  // Copy price to clipboard
  const handlePriceCopy = useCallback((e) => {
    const priceEl = e.currentTarget;
    if (priceEl.classList.contains('copied')) return;
    const text = priceEl.textContent;
    if (!text || text === '...') return;

    navigator.clipboard.writeText(text).then(() => {
      priceEl.classList.add('copied');
      setTimeout(() => priceEl.classList.remove('copied'), 1200);
    }).catch(() => {});
  }, []);

  return (
    <section id="live-price" className="price-ticker-bar">
      <div className="container-fluid">
        <div className="price-ticker-wrapper" data-weex={showWeex || undefined}>
          {filters && (
            <div className="price-ticker-filter" data-tour="filters">
              <FilterDropdown
                filters={filters}
                setWhaleTradesMinVolume={setWhaleTradesMinVolume}
                setLiquidationsMinVolume={setLiquidationsMinVolume}
                setSelectedCoins={setSelectedCoins}
                toggleCategory={toggleCategory}
                clearCategories={clearCategories}
              />
            </div>
          )}

          <div className="price-ticker-divider" />

          <div className="price-ticker-coins" ref={containerRef}>
            {hasPrices ? tickerCoins.map((coin) => (
              <div className="price-ticker-coin" key={coin.symbol}>
                <img src={coin.icon} alt={coin.symbol} width={20} height={20} draggable="false" />
                <span className="price-ticker-price" data-price={coin.symbol} onClick={handlePriceCopy} onMouseUp={handlePriceCopy}>...</span>
              </div>
            )) : Array.from({ length: tickerCoins.length || 10 }, (_, i) => (
              <div className="price-ticker-coin-skeleton" key={i}>
                <div className="skeleton-circle" />
                <div className="skeleton-bar" />
              </div>
            ))}
          </div>

          <HyperliquidPromo shape="round" />
          {showWeex && <BitunixPromo shape="round" />}
        </div>
      </div>
    </section>
  );
}

export default LivePriceTicker;
